import { useState, FormEvent } from 'react'
import { cn } from '@/lib/utils'
import { Button } from './button'
import { Mail, ArrowRight, CheckCircle, Loader2 } from 'lucide-react'

export interface LeadCaptureFormProps {
  type?: 'newsletter' | 'consultation' | 'download' | 'webinar'
  title?: string
  description?: string
  submitText?: string
  successMessage?: string
  onSubmit?: (data: { email: string; name?: string }) => Promise<void>
  className?: string
  showName?: boolean
  placeholderEmail?: string
  placeholderName?: string
}

export function LeadCaptureForm({
  type = 'newsletter',
  title,
  description,
  submitText = 'Subscribe',
  successMessage = 'Success! Check your email to confirm.',
  onSubmit,
  className,
  showName = false,
  placeholderEmail = 'Enter your email',
  placeholderName = 'Your name',
}: LeadCaptureFormProps) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [message, setMessage] = useState('')

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setStatus('loading')

    try {
      if (onSubmit) {
        await onSubmit({ email, name: showName ? name : undefined })
      }
      setStatus('success')
      setMessage(successMessage)
      setEmail('')
      setName('')
    } catch (error) {
      setStatus('error')
      setMessage('Something went wrong. Please try again.')
    }
  }

  const getDefaultTitle = () => {
    switch (type) {
      case 'consultation':
        return 'Book a Free Consultation'
      case 'download':
        return 'Download Your Free Guide'
      case 'webinar':
        return 'Register for This Webinar'
      default:
        return 'Stay Updated with AIAlgo.trading'
    }
  }

  const getDefaultDescription = () => {
    switch (type) {
      case 'consultation':
        return 'Get personalized advice on algorithmic trading strategies and implementation.'
      case 'download':
        return 'Get instant access to our comprehensive guide on algorithmic trading.'
      case 'webinar':
        return 'Reserve your spot for our upcoming live session on algorithmic trading.'
      default:
        return 'Get the latest insights, strategies, and updates delivered to your inbox.'
    }
  }

  return (
    <div className={cn('card p-6', className)}>
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-card-foreground mb-2">
          {title || getDefaultTitle()}
        </h3>
        <p className="text-muted-foreground text-sm">
          {description || getDefaultDescription()}
        </p>
      </div>

      {/* Form */}
      {status !== 'success' ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          {showName && (
            <div>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={placeholderName}
                className="input"
                required
              />
            </div>
          )}

          <div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={placeholderEmail}
              className="input"
              required
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={status === 'loading'}
          >
            {status === 'loading' ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                {submitText}
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>

          {status === 'error' && (
            <p className="text-sm text-destructive mt-2">{message}</p>
          )}

          <p className="text-xs text-muted-foreground text-center">
            We respect your privacy. Unsubscribe at any time.
          </p>
        </form>
      ) : (
        /* Success State */
        <div className="text-center py-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-success/20 mb-4">
            <CheckCircle className="h-6 w-6 text-success" />
          </div>
          <p className="text-card-foreground font-medium">{message}</p>
        </div>
      )}
    </div>
  )
}

// Inline Lead Capture - Compact version
export interface InlineLeadCaptureProps {
  placeholder?: string
  buttonText?: string
  onSubmit: (email: string) => Promise<void>
  className?: string
}

export function InlineLeadCapture({
  placeholder = 'Enter your email',
  buttonText = 'Subscribe',
  onSubmit,
  className,
}: InlineLeadCaptureProps) {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success'>('idle')

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!email) return

    setStatus('loading')
    try {
      await onSubmit(email)
      setStatus('success')
      setEmail('')
    } catch {
      setStatus('idle')
    }
  }

  if (status === 'success') {
    return (
      <div className={cn('flex items-center gap-2 text-success text-sm', className)}>
        <CheckCircle className="h-4 w-4" />
        <span>You're subscribed!</span>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className={cn('flex gap-2', className)}>
      <div className="relative flex-1">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={placeholder}
          className="input pl-10"
          required
        />
      </div>
      <Button type="submit" disabled={status === 'loading'}>
        {status === 'loading' ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          buttonText
        )}
      </Button>
    </form>
  )
}

// Newsletter Signup - For footers and sidebars
export interface NewsletterSignupProps {
  title?: string
  description?: string
  onSubmit: (email: string) => Promise<void>
  className?: string
}

export function NewsletterSignup({
  title = 'Newsletter',
  description = 'Get weekly insights on algorithmic trading strategies.',
  onSubmit,
  className,
}: NewsletterSignupProps) {
  return (
    <div className={cn('space-y-4', className)}>
      <div>
        <h4 className="font-semibold text-card-foreground mb-1">{title}</h4>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <InlineLeadCapture onSubmit={onSubmit} buttonText="Join" />
    </div>
  )
}
